import { useQuery } from '@tanstack/react-query';
import { ChevronDown, DollarSign, Ruler } from 'lucide-react';
import client from '@/api/client';
import type { Supply } from '@/types/api';
import type { ProductFormState } from './types';

interface StepPricingInventoryProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
  priceError?: string;
}

export function StepPricingInventory({
  state,
  updateState,
  priceError,
}: StepPricingInventoryProps) {
  const { data: blankSupplies } = useQuery({
    queryKey: ['supplies', 'blanks'],
    queryFn: () => client.get<Supply[]>('/supplies/blanks').then((r) => r.data),
  });

  const margin =
    state.basePrice && state.cost && parseFloat(state.basePrice) > 0
      ? (
          ((parseFloat(state.basePrice) - parseFloat(state.cost)) /
            parseFloat(state.basePrice)) *
          100
        ).toFixed(1)
      : null;

  const handleBlankSelect = (blankId: string) => {
    const blank = blankSupplies?.find((b) => b.id === blankId);
    updateState((prev) => {
      const next = { ...prev, blankSupplyId: blankId };
      if (blank) {
        if (!prev.dimensionHeight && blank.dimensionHeight != null) {
          next.dimensionHeight = String(blank.dimensionHeight);
        }
        if (!prev.dimensionWidth && blank.dimensionWidth != null) {
          next.dimensionWidth = String(blank.dimensionWidth);
        }
        if (!prev.dimensionDepth && blank.dimensionDepth != null) {
          next.dimensionDepth = String(blank.dimensionDepth);
        }
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
            Sale Price <span className="text-danger-400">*</span>
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
            <input
              type="number"
              min="0"
              step="0.01"
              value={state.basePrice}
              onChange={(e) =>
                updateState((prev) => ({ ...prev, basePrice: e.target.value }))
              }
              placeholder="0.00"
              className={`w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450 ${
                priceError ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600'
              }`}
            />
          </div>
          {priceError && <p className="mt-1.5 text-sm text-danger-400">{priceError}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
            Cost
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
            <input
              type="number"
              min="0"
              step="0.01"
              value={state.cost}
              onChange={(e) => updateState((prev) => ({ ...prev, cost: e.target.value }))}
              placeholder="0.00"
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl focus:outline-none focus:border-amber-450"
            />
          </div>
        </div>
      </div>

      {margin !== null && (
        <div
          className={`p-4 rounded-xl border ${
            parseFloat(margin) >= 30
              ? 'bg-success-500/10 border-success-500/30'
              : parseFloat(margin) >= 15
                ? 'bg-amber-450/10 border-amber-450/30'
                : 'bg-danger-500/10 border-danger-500/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-warm-sand">Profit Margin</span>
            <span className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">
              {margin}%
            </span>
          </div>
        </div>
      )}

      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          <Ruler className="w-4 h-4 text-amber-450" />
          Dimensions (H × W × D inches)
        </label>
        <div className="grid grid-cols-3 gap-3">
          {(['dimensionHeight', 'dimensionWidth', 'dimensionDepth'] as const).map((field, idx) => (
            <input
              key={field}
              type="number"
              min="0"
              step="0.01"
              value={state[field]}
              onChange={(e) =>
                updateState((prev) => ({ ...prev, [field]: e.target.value }))
              }
              placeholder={['Height', 'Width', 'Depth'][idx]}
              className="w-full px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm focus:outline-none focus:border-amber-450"
            />
          ))}
        </div>
      </div>

      {blankSupplies && blankSupplies.length > 0 && (
        <div className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl">
          <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
            Blank / Substrate
          </label>
          <div className="relative">
            <select
              value={state.blankSupplyId}
              onChange={(e) => handleBlankSelect(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm appearance-none"
            >
              <option value="">— No blank selected —</option>
              {blankSupplies.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-warm-taupe" />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          Material
        </label>
        <input
          type="text"
          value={state.material}
          onChange={(e) => updateState((prev) => ({ ...prev, material: e.target.value }))}
          placeholder="PLA, acrylic, cotton..."
          className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl focus:outline-none focus:border-amber-450"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          UPC Codes
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text"
            value={state.upcCode}
            onChange={(e) => updateState((prev) => ({ ...prev, upcCode: e.target.value }))}
            placeholder="UPC 1"
            className="w-full px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm focus:outline-none focus:border-amber-450"
          />
          <input
            type="text"
            value={state.upcCode2}
            onChange={(e) => updateState((prev) => ({ ...prev, upcCode2: e.target.value }))}
            placeholder="UPC 2"
            className="w-full px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm focus:outline-none focus:border-amber-450"
          />
          <input
            type="text"
            value={state.systemUpc}
            onChange={(e) => updateState((prev) => ({ ...prev, systemUpc: e.target.value }))}
            placeholder="System UPC"
            className="w-full px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm focus:outline-none focus:border-amber-450"
          />
        </div>
      </div>
    </div>
  );
}
